import { MarketAccess } from "@/app/MarketAccess";
import { LoginProvider } from "@/authorization/LoginProvider";
import { FallbackFullscreenErrorBoundary } from "@/components/FallbackErrorBoundary";
import { HeadingSection } from "@/components/heading/HeadingSection";
import { CalendarLayout } from "@/features/calendar/CalendarLayout";
import { ModalsProvider } from "@/features/modals/ModalsProvider";
import { PlaygroundPage } from "@/features/playground/PlaygroundPage";
import { ActiveProjectProvider } from "@/features/project/ActiveProject";
import { ManageProjectLayout } from "@/features/project/ManageProjectLayout";
import { ProjectsPage } from "@/features/projects/ProjectsPage";
import { SearchProjectProvider } from "@/features/projects/SearchProject";
import { CalendarTourProvider } from "@/features/tour/calendarTour/CalendarTourProvider";
import { ProjectTourProvider } from "@/features/tour/projectTour/ProjectTourProvider";
import { Workspace } from "@/features/workspace/Workspace";
import { CustomizeWorkspacePage } from "@/features/workspace/customize/CustomizeWorkspacePage";
import { useSentry } from "@/hooks/useSentry";
import { TranslationProvider } from "@repo/translation";
import { SettingsProvider } from "@repo/ui";
import { ToastProvider } from "@repo/ui";
import { AlertBanner } from "@repo/ui";
import { BrowserRouter, Route, Routes } from "react-router-dom";

function App() {
  useSentry();

  return (
    <TranslationProvider>
      <LoginProvider>
        <FallbackFullscreenErrorBoundary errorMessage="Something went wrong! Please reload the page.">
          <SettingsProvider>
            <ToastProvider>
              <MarketAccess>
                <BrowserRouter basename={import.meta.env.VITE__LOCALE}>
                  {/* this is necessary for showing popover on top */}
                  <div id="popper-portal" className="tour-popper-portal" />
                  {/* this is where prompts are rendered */}
                  <div id="modal-prompt" />
                  {/* this is where modal sheets are rendered */}
                  <div id="modal-portal" />
                  {/* this is where tooltips are rendered */}
                  <div id="tooltip-portal" />
                  <AlertBanner />
                  <HeadingSection>
                    <Routes>
                      <Route
                        path="/restricted"
                        element={<div>Restricted</div>}
                      />
                      <Route
                        path="/"
                        element={
                          <SearchProjectProvider>
                            <ModalsProvider>
                              <ProjectTourProvider>
                                <ProjectsPage />
                              </ProjectTourProvider>
                            </ModalsProvider>
                          </SearchProjectProvider>
                        }
                      />

                      <Route
                        path="workspace"
                        element={
                          <ModalsProvider>
                            <Workspace />
                          </ModalsProvider>
                        }
                      />
                      <Route
                        path="calendar"
                        element={
                          <ModalsProvider>
                            <CalendarTourProvider>
                              <CalendarLayout />
                            </CalendarTourProvider>
                          </ModalsProvider>
                        }
                      />
                      <Route
                        path="/opportunity/:opportunityId"
                        element={
                          <ActiveProjectProvider>
                            <ModalsProvider>
                              <ProjectTourProvider>
                                <ManageProjectLayout />
                              </ProjectTourProvider>
                            </ModalsProvider>
                          </ActiveProjectProvider>
                        }
                      />

                      <Route path="/playground" element={<PlaygroundPage />} />
                      <Route
                        path="/customize"
                        element={
                          <ModalsProvider>
                            <CustomizeWorkspacePage />
                          </ModalsProvider>
                        }
                      />
                    </Routes>
                  </HeadingSection>
                </BrowserRouter>
              </MarketAccess>
            </ToastProvider>
          </SettingsProvider>
        </FallbackFullscreenErrorBoundary>
      </LoginProvider>
    </TranslationProvider>
  );
}

export default App;
